import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { ShieldCheck, Tag, TagPlus, Ticket } from "lucide-react"
import { useTranslation } from "react-i18next"
import { ConversationFlowEnum, ConversationStatus, ConversationStatusEnum } from "schema"
import { useConversationStatuses } from "../../conversations/hooks/useConversationStatuses"

type Props = {
  status: ConversationStatus
  failedReason?: string | null
  whatsappRoute?: { senderNumber: string; recipientCallingCode: string | null } | null
  conversationFlow?: (typeof ConversationFlowEnum)[keyof typeof ConversationFlowEnum] | null
  hasTickets?: boolean
  supportStatus?: "Deflected" | "Issue" | "IssueUpdate"
}

export function ConversationStatusBadge({
  status,
  failedReason,
  whatsappRoute,
  conversationFlow,
  hasTickets,
  supportStatus,
}: Props) {
  const { t } = useTranslation()
  const { icons, labels } = useConversationStatuses()

  const routeValues = {
    senderNumber: whatsappRoute?.senderNumber ?? "-",
    recipientCallingCode: whatsappRoute?.recipientCallingCode ?? "-",
  }

  const reason = failedReason
    ? {
        whatsapp_message_variables_invalid: t(
          `conversations.failedReasons.whatsapp_message_variables_invalid`,
        ),
        whatsapp_display_name_approval_required: t(
          "conversations.failedReasons.whatsapp_display_name_approval_required",
        ),
        whatsapp_country_restricted: t(
          "conversations.failedReasons.whatsapp_country_restricted",
          routeValues,
        ),
        whatsapp_country_skipped: t(
          "conversations.failedReasons.whatsapp_country_skipped",
          routeValues,
        ),
      }[failedReason] || failedReason
    : null

  if (conversationFlow === ConversationFlowEnum.Inbound) {
    if (supportStatus === "Issue") {
      return (
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className="px-1.5">
              <Tag className="text-support-secondary" size={12} />
              <div className="flex flex-row items-center gap-1">
                {t("conversations.statuses.Issue")}
              </div>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{t("conversations.statuses.tooltips.Issue")}</TooltipContent>
        </Tooltip>
      )
    }

    if (supportStatus === "IssueUpdate") {
      return (
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className="px-1.5">
              <TagPlus className="text-testing-3" size={12} />
              <div className="flex flex-row items-center gap-1">
                {t("conversations.statuses.IssueUpdate")}
              </div>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{t("conversations.statuses.tooltips.IssueUpdate")}</TooltipContent>
        </Tooltip>
      )
    }

    if (supportStatus === undefined && hasTickets) {
      return (
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className="px-1.5">
              <Ticket className="text-support" size={12} />
              <div className="flex flex-row items-center gap-1">
                {t("conversations.statuses.Ticket")}
              </div>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{t("conversations.statuses.tooltips.Ticket")}</TooltipContent>
        </Tooltip>
      )
    }

    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline" className="px-1.5">
            <ShieldCheck className="text-success" size={12} />
            <div className="flex flex-row items-center gap-1">
              {t("conversations.statuses.Deflected")}
            </div>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{t("conversations.statuses.tooltips.Deflected")}</TooltipContent>
      </Tooltip>
    )
  }

  const tooltip =
    status === ConversationStatusEnum.Failed && reason
      ? reason
      : t(`conversations.statuses.tooltips.${status}`)

  return (
    <div className="flex flex-row items-center gap-1.5">
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline" className="px-1.5">
            {icons[status]}
            <div className="flex flex-row items-center gap-1">{labels[status]}</div>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </div>
  )
}
